<template>
  <div class="step-container client" :class="containerClass">
    <help-drawer :title="helpTitle">
      <div>
        <h4 class="pb-2">What is a beneficiary?</h4>
        <p>
          A <strong>beneficiary</strong> can be any person or entity the owner
          chooses to receive their <strong>account assets</strong> after he
          or she dies. The total allocated to all beneficiaries must add up to
          100%.
          <a target="_blank" href="https://irs.gov">IRS.gov</a>
        </p>
        <h4 class="pb-2">What are Primary and Contingent beneficiaries?</h4>
        <p>
          A <strong>primary</strong> beneficiary will be the first in line to
          receive your assets.<br />
          <strong>Contingent beneficiaries</strong> are also important because
          they will receive your assets if none of your
          <strong>primary beneficiaries</strong>
          are alive at the time of your passing.
        </p>
      </div>
    </help-drawer>
    <allocation-dialog
      :dialog="showDialog"
      @dialog-closed="takeDialogAction"
    ></allocation-dialog>
    <v-form name="AccountBeneficiaries" class="form" data-test-id="primary-beneficiaries-form">
      <v-container class="client-step-container beneficiary-container">
        <syncing-indicator :pageName="MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id" :instance="instance"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="primary-beneficiaries-header">
              <span class="headerlight">
                Would you like to
                <strong> add Primary Beneficiaries </strong>
                to
                <strong>
                  {{ accountName }} ({{
                    label
                  }}) account?
                </strong>
              </span>
            </h1>
            <div data-test-id="contingent-info">You can add contingent beneficiaries on the next page.</div>
            <div class="pt-2 help-link">
              <a @click="toggleHelp()" data-test-id="help-link"> {{ helpTitle }} </a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your information...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isAccountPrimaryBeneficiaryComplete(instance) && formHidden && !isSyncing"
          :submitted="isAccountPrimaryBeneficiaryComplete(instance)"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container radio-container mb-4">
              <v-radio-group
                v-model="addAccountBeneficiaries"
                :error-messages="accountBeneficiaryErrors"
                hide-details="auto"
                @change="$v.addAccountBeneficiaries.$touch()"
                data-test-id="add-beneficiaries-radio-group"
              >
                <v-radio
                  :ripple="false"
                  class="v-faux-radio mb-4"
                  label="Yes"
                  :value="true"
                  data-test-id="add-beneficiaries-yes"
                ></v-radio>
                <div class="expander top" v-show="addAccountBeneficiaries">
                  <!-- Linked Beneficiary -->
                  <div v-if="hasLinkedClient">
                    <label for="primaryBeneficiary" data-test-id="linked-beneficiary-label">
                      Would you like to add your co-applicant as a Primary Beneficiary?
                    </label>
                    <v-radio-group
                      v-model="linkedClientPrimary"
                      :error-messages="linkedClientPrimaryErrors"
                      hide-details="auto"
                      row
                      @change="$v.linkedClientPrimary.$touch()"
                      data-test-id="linked-beneficiary-radio-group"
                    >
                      <v-radio
                        :ripple="false"
                        class="v-faux-radio half-width first"
                        label="Yes"
                        :value="true"
                        data-test-id="linked-beneficiary-yes"
                      ></v-radio>
                      <v-radio
                        :ripple="false"
                        class="v-faux-radio half-width"
                        label="No"
                        :value="false"
                        data-test-id="linked-beneficiary-no"
                      ></v-radio>
                    </v-radio-group>
                    <div class="expander mt-3" v-show="linkedClientPrimary">
                      <v-row>
                        <v-col class="col-dob">
                          <label for="dob" data-test-id="linked-beneficiary-dob-label">Date of Birth</label>
                          <v-text-field
                            v-model="linkedPrimaryBeneficiary.dob"
                            :placeholder="INPUT_DATE_FMT"
                            outlined
                            name="dob"
                            maxlength="8"
                            :backgroundColor="inputBackgroundColor"
                            @input="$v.linkedPrimaryBeneficiary.dob.$touch()"
                            @blur="standardizeDate()"
                            :error-messages="linkedPrimaryBeneficiaryDobErrors"
                            dense
                            inputmode="numeric"
                            data-test-id="linked-beneficiary-dob-input"
                          >
                            <template v-slot:append>
                              <span class="cal-icon" data-test-id="calendar-icon">
                                <v-icon small color="#9b9b9b">
                                  fas fa-calendar-alt
                                </v-icon>
                              </span>
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col class="col-allocation-amt">
                          <label data-test-id="allocation-label"> Allocation Amount </label>
                          <allocation-picker
                            :value="linkedPrimaryBeneficiary.allocationPercent"
                            :pickerType="PICKER_TYPES.ALLOCATION"
                            @update-number="
                              updateAllocation($event, linkedPrimaryBeneficiary)
                            "
                            data-test-id="allocation-picker"
                          ></allocation-picker>
                        </v-col>
                      </v-row>
                    </div>
                  </div>
                  <!-- Alternate Beneficiary -->
                  <div
                    v-for="(beneficiary, index) in alternateBeneficiaries"
                    :key="beneficiary"
                    class="beneficiary-block"
                    :data-test-id="'alternate-beneficiary-' + index"
                  >
                    <div class="remove-area">
                      <span @click="removeAlternateBeneficiary(index)" data-test-id="remove-beneficiary">
                        <v-icon small :color="textColor"> fas fa-trash </v-icon>
                      </span>
                    </div>
                    <v-row>
                      <v-col class="col-first-name">
                        <label for="firstName" class="required" data-test-id="first-name-label">
                          First Name
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="256"
                          name="firstName"
                          aria-required="true"
                          v-model.trim="beneficiary.firstName"
                          :backgroundColor="inputBackgroundColor"
                          @input="
                            $v.alternateBeneficiaries.$each[
                              index
                            ].firstName.$touch()
                          "
                          @blur="
                            $v.alternateBeneficiaries.$each[
                              index
                            ].firstName.$touch()
                          "
                          :error-messages="
                            alternateBeneficiaryFirstNameErrors(index)
                          "
                          hide-details="auto"
                          class="mb-2"
                          :data-test-id="'first-name-input-' + index"
                        />
                      </v-col>
                      <v-col class="col-last-name">
                        <label for="lastName" class="required" data-test-id="last-name-label">
                          Last Name
                        </label>
                        <v-text-field
                          outlined
                          type="text"
                          maxlength="256"
                          name="lastName"
                          aria-required="true"
                          v-model.trim="beneficiary.lastName"
                          :backgroundColor="inputBackgroundColor"
                          :data-test-id="'last-name-input-' + index"
                          @input="
                            $v.alternateBeneficiaries.$each[
                              index
                            ].lastName.$touch()
                          "
                          @blur="
                            $v.alternateBeneficiaries.$each[
                              index
                            ].lastName.$touch()
                          "
                          :error-messages="
                            alternateBeneficiaryLastNameErrors(index)
                          "
                          hide-details="auto"
                          class="mb-2"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <label for="dob">Date of Birth</label>
                        <v-text-field
                          v-model="beneficiary.dob"
                          :placeholder="INPUT_DATE_FMT"
                          name="dob"
                          maxlength="8"
                          outlined
                          :backgroundColor="inputBackgroundColor"
                          @input="
                            $v.alternateBeneficiaries.$each[index].dob.$touch()
                          "
                          @blur="standardizeDate(index)"
                          :error-messages="alternateBeneficiaryDobErrors(index)"
                          dense
                          inputmode="numeric"
                        >
                          <template v-slot:append>
                            <span class="cal-icon">
                              <v-icon small color="#9b9b9b">
                                fas fa-calendar-alt
                              </v-icon>
                            </span>
                          </template>
                        </v-text-field>
                      </v-col>
                      <v-col class="col-allocation-amt">
                        <label> Allocation Amount </label>
                        <allocation-picker
                          :value="beneficiary.allocationPercent"
                          :pickerType="PICKER_TYPES.ALLOCATION"
                          @update-number="updateAllocation($event, beneficiary)"
                        ></allocation-picker>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="col-relation">
                        <label for="relation">Relation Status</label>
                         <div>
                          <v-select
                            v-model="beneficiary.relationStatus"
                            :items="relationStatusOptions"
                            :backgroundColor="inputBackgroundColor"
                            ref="relationStatusList"
                            outlined
                            aria-required="true"
                            placeholder="Choose Relation Status"
                            dense
                            hide-details="auto"
                            @change="$v.alternateBeneficiaries.$each[index].relationStatus.$touch()"
                            :error-messages="alternateBeneficiaryRelationStatusErrors(index)"
                            data-test-id="relation-status-select"
                          >
                          </v-select>
                      </div>
                      </v-col>
                    </v-row>
                    <div class="error-area" v-if="exceedsAllocationMax">
                      Total Allocations cannot exceed 100%
                    </div>
                  </div>
                  <div class="add-area">
                    <a
                      @click="addAlternateBeneficiary"
                      v-if="!hasLinkedClient || linkedClientPrimary !== null"
                    >
                      <i class="fas fa-plus"></i>
                      Add
                      {{ this.allBeneficiaries.length > 0 ? 'another' : '' }}
                      Primary Beneficiary
                    </a>
                  </div>
                </div>
                <v-radio
                  :ripple="false"
                  class="v-faux-radio"
                  label="No"
                  :value="false"
                ></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>

          <div class="skip-for-now">
            <a @click="skip()">Skip for now</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <allocation-tracker :beneficiaries="allBeneficiaries"></allocation-tracker>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import {
  MENUS,
  formatDate,
  US_DATE_SHORT,
  ISO_DATE_SHORT,
  INPUT_DATE_FMT,
  validInputDate,
  PICKER_TYPES,
  DIALOG_DECISIONS,
  ACCOUNT_TYPES,
  DB_ACCOUNT_TYPES,
  BENEFICIARY_RELATION_STATUS
} from '@/common/constants';
import AllocationPicker from '@/components/common/ValuePicker.vue';
import AllocationTracker from '@/components/client/AllocationTracker';
import AllocationDialog from '@/components/client/dialog/AllocationDialog';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

const ALLOCATION_DEFAULT = 0;

// Custom validator for allocation totals
const allocationMin = (value, vm) => {
  if (!vm.addAccountBeneficiaries || vm.allBeneficiaries.length === 0)
    return true;
  return vm.allocationTotals === 100;
};

// Custom validator for number of beneficiaries
const atLeastOneBeneficiary = (value, vm) => {
  if (!vm.addAccountBeneficiaries) return true;

  return vm.allBeneficiaries.length > 0;
};

export default {
  name: 'AccountPrimaryBeneficiaries',
  props: {
    name: String,
    label: String,
  },

  components: {
    BottomNav,
    HelpDrawer,
    DataSubmittedMessage,
    AllocationPicker,
    AllocationTracker,
    AllocationDialog,
    SyncingIndicator
  },
  mixins: [InterviewClientMixin],

  data: () => ({
    addAccountBeneficiaries: null,
    linkedClientPrimary: false,
    maxDate: formatDate(window.moment()),
    linkedPrimaryBeneficiary: {
      dob: null,
      allocationPercent: ALLOCATION_DEFAULT,
      // relationStatus: null,
    },
    alternateBeneficiaries: [],
    showDialog: false,
    MENUS: MENUS,
    PICKER_TYPES: PICKER_TYPES,
    DIALOG_DECISIONS: DIALOG_DECISIONS,
  }),

  validations() {
    return {
      addAccountBeneficiaries: {
        required,
        allocationMin,
        atLeastOneBeneficiary,
      },
      linkedClientPrimary: {
        required: requiredIf(
          () => this.addAccountBeneficiaries && this.hasLinkedClient,
        ),
      },

      linkedPrimaryBeneficiary: {
        dob: {
          validInputDate,
        },
        allocationPercent: {},
        // relationStatus: {
        //   required: requiredIf(() => this.linkedClientPrimary)
        // }
      },
      alternateBeneficiaries: {
        $each: {
          firstName: {
            required: requiredIf(() => this.addAccountBeneficiaries),
          },
          lastName: {
            required: requiredIf(() => this.addAccountBeneficiaries),
          },
          dob: {
            validInputDate,
          },
          allocationPercent: {},
          relationStatus: {
            required: requiredIf(() => this.addAccountBeneficiaries)
          }
        },
      },
      validationGroup: [
        'addAccountBeneficiaries',
        'linkedClientPrimary',
        'alternateBeneficiaries',
      ],
    };
  },
  watch: {
    alternateBeneficiaries: {
      deep: true,
      handler() {
        this.alternateBeneficiaries.forEach((b) => {
          const name = `${b.firstName ? b.firstName : ''} ${
            b.lastName ? b.lastName : ''
          }`;
          this.$set(b, 'name', name);
        });
      },
    },
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    isAccountPrimaryBeneficiaryComplete: {
      handler(newVal) {
        if (newVal) {
          this.formHidden = true;
        }
      },
      immediate: true
    },
  },
  computed: {
    /* Dynamic errors */
    accountBeneficiaryErrors() {
      const errors = [];
      if (!this.$v.addAccountBeneficiaries.$dirty) return errors;
      !this.$v.addAccountBeneficiaries.required &&
        errors.push('A selection is required');
      !this.$v.addAccountBeneficiaries.atLeastOneBeneficiary &&
        errors.push('A beneficiary must be chosen');
      !this.$v.addAccountBeneficiaries.allocationMin &&
        errors.push('Total allocations must equal 100%');

      return errors;
    },
    linkedClientPrimaryErrors() {
      const errors = [];
      if (!this.$v.linkedClientPrimary.$dirty) return errors;
      !this.$v.linkedClientPrimary.required &&
        errors.push('A selection is required');
      return errors;
    },
    linkedPrimaryBeneficiaryDobErrors() {
      const errors = [];
      if (!this.$v.linkedPrimaryBeneficiary.dob.$dirty) return errors;
      !this.$v.linkedPrimaryBeneficiary.dob.validInputDate &&
        errors.push('DOB must be a valid date (MMDDYYYY)');
      return errors;
    },
    linkedPrimaryBeneficiaryDOBString() {
      return this.linkedPrimaryBeneficiary.dob
        ? window
            .moment(this.linkedPrimaryBeneficiary.dob, INPUT_DATE_FMT, true)
            .format(ISO_DATE_SHORT)
        : null;
    },

    allBeneficiaries() {
      const all = [];
      if (this.formHidden || !this.addAccountBeneficiaries) return all;

      if (this.linkedClientPrimary === true)
        all.push(this.linkedPrimaryBeneficiary);
      this.alternateBeneficiaries.forEach((b) => all.push(b));
      return all;
    },
    hasLinkedClient() {
      // Security: Co-applicant data removed from API
      // Feature disabled - users must add beneficiaries manually
      return false;
    },
    helpTitle() {
      return "What's a primary & contingent beneficiary?";
    },
    accountName() {
      if (this.accountType === DB_ACCOUNT_TYPES.IRA) {
        return ACCOUNT_TYPES.IRA;
      } else if (this.accountType === DB_ACCOUNT_TYPES.ROTH_IRA) {
        return ACCOUNT_TYPES.ROTH_IRA;
      } else if (this.accountType === DB_ACCOUNT_TYPES.SEP_IRA) {
        return ACCOUNT_TYPES.SEP_IRA;
      } else if (this.accountType === DB_ACCOUNT_TYPES.SINGLE_NAME_BROKERAGE) {
        return ACCOUNT_TYPES.SINGLE_NAME_BROKERAGE;
      } else if (this.accountType === DB_ACCOUNT_TYPES.JOINT_NAME_BROKERAGE) {
        return ACCOUNT_TYPES.JOINT_NAME_BROKERAGE;
      } else if (this.accountType === DB_ACCOUNT_TYPES.OFFLINE_TRUST) {
        return 'Trust';
      } else if (this.accountType === DB_ACCOUNT_TYPES.OFFLINE_INHERITED_IRA) {
        return 'Inherited IRA';
      } else if (this.accountType === DB_ACCOUNT_TYPES.OFFLINE_SIMPLE_IRA) {
        return 'Simple IRA';
      } else if (this.accountType === DB_ACCOUNT_TYPES.OFFLINE_CORPORATE) {
        return 'Corporate';
      } else if (this.accountType === DB_ACCOUNT_TYPES.OFFLINE_OTHER) {
        return 'Other';
      } else if (this.accountType === DB_ACCOUNT_TYPES.MANAGED_IRA) {
        return ACCOUNT_TYPES.MANAGED_IRA;
      } else if (this.accountType === DB_ACCOUNT_TYPES.MANAGED_JOINT) {
        return ACCOUNT_TYPES.MANAGED_JOINT;
      } else if (this.accountType === DB_ACCOUNT_TYPES.MANAGED_ROTH_IRA) {
        return ACCOUNT_TYPES.MANAGED_ROTH_IRA;
      } else if (this.accountType === DB_ACCOUNT_TYPES.MANAGED_TRUST) {
        return ACCOUNT_TYPES.MANAGED_TRUST;
      } else if (this.accountType === DB_ACCOUNT_TYPES.MANAGED_BROKERAGE) {
        return ACCOUNT_TYPES.MANAGED_BROKERAGE;
      }
      // Default fallback for any other account types
      return this.accountType;
    },
    allocationTotals() {
      let tots = 0;
      const benes = this.allBeneficiaries || [];
      benes.forEach((b) => {
        tots += b.allocationPercent ? parseInt(b.allocationPercent) : 0;
      });
      return tots;
    },
    exceedsAllocationMax() {
      return (
        this.addAccountBeneficiaries &&
        this.allBeneficiaries.length > 0 &&
        this.allocationTotals > 100
      );
    },
    containerClass() {
      return this.allBeneficiaries.length > 0 ? 'has-beneficiaries' : '';
    },
    instance() {
      return {
        name: this.name,
        label: this.label
      };
    },
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id, this.instance);
      return page && page.status === 'IN_PROGRESS';
    },
    formHidden() {
      return this.$store.state.client.formHidden;
    },
  },

  methods: {
    // Standardize a date from input format to US Readable
    standardizeDate(index = -1) {
      // Linked Primary
      if (index === -1) {
        this.$v.linkedPrimaryBeneficiary.dob.$touch();
        if (validInputDate(this.linkedPrimaryBeneficiary.dob)) {
          this.linkedPrimaryBeneficiary.dob = this.linkedPrimaryBeneficiary.dob
            ? window
                .moment(this.linkedPrimaryBeneficiary.dob, INPUT_DATE_FMT, true)
                .format(US_DATE_SHORT)
            : null;
        }
      } else {
        // Alternate Beneficiary
        this.$v.alternateBeneficiaries.$each[index].dob.$touch();
        if (validInputDate(this.alternateBeneficiaries[index].dob)) {
          this.alternateBeneficiaries[index].dob = this.alternateBeneficiaries[
            index
          ].dob
            ? window
                .moment(
                  this.alternateBeneficiaries[index].dob,
                  INPUT_DATE_FMT,
                  true,
                )
                .format(US_DATE_SHORT)
            : null;
        }
      }
    },

    /* Validatons for a beneficiary instance first name */
    alternateBeneficiaryFirstNameErrors(idx) {
      const errors = [];
      if (!this.$v.alternateBeneficiaries.$each[idx].firstName.$dirty)
        return errors;
      !this.$v.alternateBeneficiaries.$each[idx].firstName.required &&
        errors.push('First Name is required');
      return errors;
    },

    /* Validatons for a beneficiary instance last name */
    alternateBeneficiaryLastNameErrors(idx) {
      const errors = [];
      if (!this.$v.alternateBeneficiaries.$each[idx].lastName.$dirty)
        return errors;
      !this.$v.alternateBeneficiaries.$each[idx].lastName.required &&
        errors.push('Last Name is required');
      return errors;
    },

    /* Validatons for a beneficiary instance DOB */
    alternateBeneficiaryDobErrors(idx) {
      const errors = [];
      if (!this.$v.alternateBeneficiaries.$each[idx].dob.$dirty) return errors;
      !this.$v.alternateBeneficiaries.$each[idx].dob.validInputDate &&
        errors.push('DOB must be a valid date (MMDDYYYY)');
      return errors;
    },

    /* Validatons for a beneficiary instance Relation Status */
    alternateBeneficiaryRelationStatusErrors(idx) {
      const errors = [];
      if (!this.$v.alternateBeneficiaries.$each[idx].relationStatus.$dirty) return errors;
      !this.$v.alternateBeneficiaries.$each[idx].relationStatus.required &&
        errors.push('Relation Status is required');
      return errors;
    },

    /* Add a beneficiary instance */
    addAlternateBeneficiary() {
      this.alternateBeneficiaries.push({
        firstName: null,
        lastName: null,
        dob: null,
        allocationPercent: ALLOCATION_DEFAULT,
        activatorMenu: false,
        //dobString: null,
      });
    },

    /* Remove a beneficiary instance */
    removeAlternateBeneficiary(idx) {
      this.alternateBeneficiaries.splice(idx, 1);
    },

    /* Update the allocation percentage for a beneficiary instance */
    updateAllocation(num, beneficiary) {
      beneficiary.allocationPercent = num;
    },

    /* Update available screens based on answers */
    updateScreens() {
      if (this.addAccountBeneficiaries) {
        // Add (Potentialy) any required pages
        // this.addPage(
        //   MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
        //   this.instance,
        // );
        this.interviewPages.addContingentBeneficiaryPage(this.instance.name, this.instance.label);
      } else {
        // Remove any pages no longer required
        // this.removePage(
        //   MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
        //   this.instance,
        // );
        this.interviewPages.removeContingentBeneficiaryPage(this.instance.label);
      }
    },

    /* Nav to Next Page in flow */
    next(route = null) {      
      let to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid || this.exceedsAllocationMax) {
          return;
        }
      } else {
        this.goNext(to);
        return;
      }

      // Recalculate screens/next route based on new data
      this.updateScreens();
      const updatedPages = this.interviewPages.getInterviewPages();
      
      to = route || updatedPages[this.pageIndex + 1];
      this.leaveRoute = to;

      // If no beneficiaries show warning dialog
      if (
        !this.addAccountBeneficiaries ||
        (this.addAccountBeneficiaries && this.allBeneficiaries.length === 0)
      ) {
        this.showDialog = true;
      } else {
        // Else leave
        this.dialog = false;
        this.leave();
      }
    },

    /* Navigate from page */
    leave() {
      const route = this.leaveRoute;

      let data = {
        instance: this.instance,
      };
      if (this.addAccountBeneficiaries) {
        data.beneficiaries = [];
        // Security: Co-applicant pre-fill removed
        // Users must manually enter beneficiary details

        this.alternateBeneficiaries.forEach((b) => {
          const dobString = b.dob ? formatDate(b.dob, US_DATE_SHORT) : null;
          data.beneficiaries.push({
            firstName: b.firstName,
            lastName: b.lastName,
            dob: dobString,
            allocationAmount: b.allocationPercent,
            relationStatus: b.relationStatus
          });
        });
      }

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id, this.instance);

      this.completeStep(
        MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id,
        data,
        this.instance,
      )
        .then(() => {
          this.formHidden = true;
          this.goNext(route);
        })
        .catch(() => {});
    },

    /* Take action from leave warning dialog */
    takeDialogAction(decision) {
      this.showDialog = false;

      // If save - then leave
      if (decision === DIALOG_DECISIONS.SAVE) {
        this.leave();
      } else if (decision === DIALOG_DECISIONS.SKIP) {
        // If skip, then skip
        this.skip();
      }
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {      
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },

    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isAccountPrimaryBeneficiaryComplete(this.instance)) {
        this.formHidden = true;
      }
    }
  },

  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex;
    let i = 0;
    for (const page of this.pages) {
      if (typeof page === 'object' && window.location.pathname.includes(page.pathname.replaceAll(' ', '%20'))) {
        this.pageIndex = i;
      }
      i++;
    }
    // Initialize form data for current interview
    this.accountType = this.name;
    this.PICKER_TYPES = PICKER_TYPES;
    this.INPUT_DATE_FMT = INPUT_DATE_FMT;
    this.formHidden = this.isAccountPrimaryBeneficiaryComplete(this.instance);
    this.linkedPrimaryBeneficiary.name = this.linkedClient;

    // Initialize relation status options
    this.relationStatusOptions = [];
    Object.keys(BENEFICIARY_RELATION_STATUS).map((key) => {
      const val = BENEFICIARY_RELATION_STATUS[key];    
      this.relationStatusOptions.push(val);
    });
  },

  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // If primary beneficiaries is complete, hide the form
      if (this.isAccountPrimaryBeneficiaryComplete(this.instance)) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  },
};
</script>
<style lang="scss" scoped>
.cal-icon {
  line-height: 32px;
  margin-right: 8px;
}
.expander {
  display: flex;
  flex-direction: column;

  &.top {
    margin-bottom: 20px;
    padding-bottom: 20px;
  }
  .row {
    margin: -12px 0;

    @media screen and (max-width: 960px) {
      margin: -12px;
    }
  }

  .cb-label {
    margin-top: 5px;
  }

  .col {
    padding: 12px 0;

    &.col-first-name,
    &.col-dob {
      padding-right: 6px;
    }
    &.col-last-name,
    &.col-allocation-amt {
      padding-left: 6px;
    }

    &.col-relation {
      padding-top: 0;
    }

    @media screen and (max-width: 960px) {
      padding: 12px;
    }
  }
}
.half-width {
  width: calc(50% - 4px);
  overflow: hidden;
  margin: 0 0 0 4px !important;

  &.first {
    margin: 0 4px 0 0 !important;
  }
}
.has-beneficiaries {
  height: calc(100vh - 242px);
}
.beneficiary-container {
  padding-bottom: 60px;
}
.beneficiary-block {
  margin-top: 15px;
  padding-top: 10px;
  padding-bottom: 20px;
  border-top: 1px solid var(--secondary-color);
}
.add-area {
  margin-top: 10px;
}
.remove-area {
  display: flex;
  justify-content: flex-end;
}
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
